import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { v4 as uuidv4 } from 'uuid';
import { MagnifyingGlass, MapPin } from '@phosphor-icons/react';

/**
 * PlaceAutocomplete — server-side proxy to Google Places (New).
 * Generates a session token per interaction to optimize Google's billing.
 * REMINDER: DO NOT HARDCODE THE URL. We call our backend proxy only.
 */
const PlaceAutocomplete = ({ near, types, onPlaceSelected, placeholder = 'Search for a venue' }) => {
  const [q, setQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const tokenRef = useRef(null);

  const ensureToken = () => {
    if (!tokenRef.current) tokenRef.current = uuidv4();
    return tokenRef.current;
  };

  useEffect(() => {
    if (!q.trim() || picked) { setSuggestions([]); return undefined; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { q, session_token: ensureToken() };
        if (near) params.near = `${near.lat},${near.lng}`;
        if (types && types.length) params.types = types.join(',');
        const { data } = await api.get('/places/autocomplete', { params, signal: ctrl.signal });
        setSuggestions(data.suggestions || []);
      } catch (e) { /* aborted or err */ }
      finally { setLoading(false); }
    }, 280);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, near?.lat, near?.lng, picked]); // eslint-disable-line

  const choose = async (s) => {
    setPicked(s);
    setQ(s.primary_text);
    setSuggestions([]);
    try {
      const pid = s.place_id.replace('places/', '');
      const { data } = await api.get(`/places/details/${encodeURIComponent(pid)}`,
                                       { params: { session_token: ensureToken() } });
      onPlaceSelected?.(data);
    } catch (e) { /* ignore */ }
    tokenRef.current = null; // session ends
  };

  return (
    <div className="relative" data-testid="place-autocomplete">
      <div className="relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" size={16} />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setPicked(null); }}
          placeholder={placeholder}
          data-testid="place-input"
          className="w-full pl-9 pr-3 py-3 rounded-2xl glass-hi text-sm text-white placeholder-white/30 outline-none focus:ring-2 focus:ring-bx-cyan/40"
        />
      </div>
      {suggestions.length > 0 && (
        <div className="absolute z-30 mt-1 w-full glass rounded-2xl overflow-hidden border border-white/10">
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              data-testid={`place-suggestion-${s.place_id}`}
              onClick={() => choose(s)}
              className="w-full text-left p-3 hover:bg-white/5 flex items-start gap-2 border-b border-white/5 last:border-b-0">
              <MapPin size={14} weight="duotone" className="text-bx-cyan mt-0.5" />
              <div>
                <div className="text-sm font-bold">{s.primary_text}</div>
                <div className="text-[11px] text-white/65">{s.secondary_text}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      {loading && <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">listening…</div>}
    </div>
  );
};

export default PlaceAutocomplete;
